// client/src/components/NFTRegisterForm.js
import React, { useState } from 'react';
import { registerNFT } from './api';
import './NFTRegisterForm.css';

const NFTRegisterForm = ({ onCancel }) => {
  const [formData, setFormData] = useState({
    collectionName: '',
    tokenId: '',
    description: '', // 'title' -> 'description'
    detailed_description: '', // 'description' -> 'detailed_description'
    youtubeUri: ''
  });
  const [nftImage, setNftImage] = useState(null);
  const [subImage, setSubImage] = useState(null);
  const [nftImagePreview, setNftImagePreview] = useState(null);
  const [subImagePreview, setSubImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNftImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNftImage(file);
      setNftImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSubImage(file);
      setSubImagePreview(URL.createObjectURL(file));
    }
  };

  // 이미지 드래그 오버 핸들러
  const handleImageDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('drag-over');
  };

  // 이미지 드래그 리브 핸들러
  const handleImageDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('drag-over');
  };

  // NFT 이미지 드롭 핸들러
  const handleNftImageDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('drag-over');

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // 이미지 파일인지 확인
      if (file.type.startsWith('image/')) {
        setNftImage(file);
        setNftImagePreview(URL.createObjectURL(file));
        setMessage({ type: '', text: '' }); // 에러 메시지 초기화
        
        // 중요: input 요소의 required 검증을 우회하기 위해 input에 파일 할당
        const inputElement = document.getElementById('nftImageInput');
        if (inputElement) {
          // DataTransfer 객체를 사용하여 input에 파일 할당
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          inputElement.files = dataTransfer.files;
        }
      } else {
        setMessage({ type: 'error', text: '이미지 파일만 업로드할 수 있습니다.' });
      }
      
      // dataTransfer 초기화
      e.dataTransfer.clearData();
    }
  };

  // Sub 이미지 드롭 핸들러
  const handleSubImageDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('drag-over');

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // 이미지 파일인지 확인
      if (file.type.startsWith('image/')) {
        setSubImage(file);
        setSubImagePreview(URL.createObjectURL(file));
        setMessage({ type: '', text: '' }); // 에러 메시지 초기화
      } else {
        setMessage({ type: 'error', text: '이미지 파일만 업로드할 수 있습니다.' });
      }
      
      // dataTransfer 초기화
      e.dataTransfer.clearData();
    }
  };

  const handleDeleteSubImage = () => {
    setSubImage(null);
    setSubImagePreview(null);
  };

  const handleSubmit = async (e) => {
    console.log('NFT 등록 폼 제출 시도');

    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.collectionName || !formData.tokenId) {
      setMessage({ type: 'error', text: 'Collection Name과 Token ID는 필수입니다.' });
      return;
    }

    if (!nftImage) {
      setMessage({ type: 'error', text: 'NFT 이미지는 필수입니다.' });
      return;
    }

    console.log(`nftImage: ${nftImage.name}, subImage: ${subImage ? subImage.name : '없음'}`);

    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('collectionName', formData.collectionName);
      submitData.append('tokenId', formData.tokenId);
      submitData.append('description', formData.description);
      submitData.append('detailed_description', formData.detailed_description);
      submitData.append('youtubeUri', formData.youtubeUri);
      submitData.append('nftImage', nftImage);
      if (subImage) {
        submitData.append('subImage', subImage);
      }

      const result = await registerNFT(submitData);
      
      setMessage({ type: 'success', text: result.message || 'NFT가 성공적으로 등록되었습니다!' });
      
      // 폼 초기화
      setFormData({
        collectionName: '',
        tokenId: '',
        description: '',
        detailed_description: '',
        youtubeUri: ''
      });
      setNftImage(null);
      setSubImage(null);
      setNftImagePreview(null);
      setSubImagePreview(null);

    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.error || '등록 중 오류가 발생했습니다.' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-form-container">
      <form onSubmit={handleSubmit} className="register-form-wrapper">

        <div className="header-actions">
          <button type="button" onClick={onCancel} className="back-btn">← 뒤로가기</button>
          <h2>NFT 등록</h2>
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? '등록 중...' : '💾 NFT 등록'}
          </button>
        </div>
        
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="register-form">
          <div className="register-form-content">
            {/* 상단 정보 */}
            <div className="top-info">
              <div className="top-info-item">
                <input
                  type="text"
                  name="collectionName"
                  value={formData.collectionName}
                  onChange={handleInputChange}
                  placeholder="Collection Name *"
                  className="top-input"
                  required
                />
              </div>
              <div className="top-info-item">
                <input
                  type="text"
                  name="tokenId"
                  value={formData.tokenId}
                  onChange={handleInputChange}
                  placeholder="Token ID *"
                  className="top-input"
                  required
                />
              </div>
            </div>

            {/* Description 섹션 */}
            <div className="description-section">
              <h3>Description</h3>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="NFT 제목/요약 설명을 입력하세요"
                className="description-input"
              />
            </div>

            {/* 메인 컨텐츠 */}
            <div className="register-body">
              <div className="left-section">
                {/* NFT Image */}
                <div 
                  className="main-image"
                  onDragOver={handleImageDragOver}
                  onDragLeave={handleImageDragLeave}
                  onDrop={handleNftImageDrop}
                >
                  <h3>NFT Image *</h3>
                  {nftImagePreview ? (
                    <img src={nftImagePreview} alt="NFT Preview" />
                  ) : (
                    <div className="no-image">
                      <p>이미지를 선택하거나 드래그하세요</p>
                    </div>
                  )}
                  <div className="image-upload-control">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleNftImageChange}
                      id="nftImageInput"
                      className="file-input"
                      required
                    />
                    <label htmlFor="nftImageInput" className="file-label">
                      📁 이미지 선택
                    </label>
                  </div>
                </div>

                {/* Sub Image */}
                <div 
                  className="sub-image"
                  onDragOver={handleImageDragOver}
                  onDragLeave={handleImageDragLeave}
                  onDrop={handleSubImageDrop}
                >
                  <h3>Sub Image</h3>
                  {subImagePreview ? (
                    <img src={subImagePreview} alt="Sub Preview" />
                  ) : (
                    <div className="no-image">
                      <p>서브 이미지를 선택하거나 드래그하세요 (선택사항)</p>
                    </div>
                  )}
                  <div className="image-upload-control">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleSubImageChange}
                      id="subImageInput"
                      className="file-input"
                    />
                    <label htmlFor="subImageInput" className="file-label">
                      📁 이미지 선택
                    </label>
                    {subImagePreview && (
                      <button 
                        type="button"
                        onClick={handleDeleteSubImage}
                        className="delete-image-btn"
                      >
                        🗑️ 삭제
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="right-section">
                {/* Detailed Description */}
                <div className="detailed-description-box">
                  <h3>Detailed Description</h3>
                  <textarea
                    name="detailed_description"
                    value={formData.detailed_description}
                    onChange={handleInputChange}
                    placeholder="NFT의 상세 설명을 입력하세요&#10;&#10;💡 팁:&#10;- 이미지 URL을 한 줄에 입력하면 이미지로 표시됩니다&#10;- YouTube URL을 한 줄에 입력하면 동영상이 임베드됩니다&#10;- HTML 또는 Markdown 파일을 드래그 앤 드롭하여 내용을 로드할 수 있습니다."
                    className="detailed-description-textarea"
                    rows="15"
                  />
                </div>
              </div>
            </div>

            {/* YouTube 섹션 */}
            <div className="youtube-section">
              <h3>Youtube link</h3>
              <input
                type="url"
                name="youtubeUri"
                value={formData.youtubeUri}
                onChange={handleInputChange}
                placeholder="YouTube 링크를 입력하세요"
                className="youtube-input"
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default NFTRegisterForm;